import React, { useContext, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DataContext } from "../dataContext";
import DoubleIcon from "../components/DoubleIcon";
import TransferDetails from "../components/TransferDetails";
import SendSms, { clickSendSms } from "../components/SendSMS";
import { ReactComponent as Check } from "../assets/ico_check.svg";
import { ReactComponent as Info } from "../assets/ico_info.svg";
import { ReactComponent as Copy } from "../assets/ico_copy.svg";
import BottomButton from "../components/BottomButton";
import { isSMSCapable } from "../utils/utils";

const TransferSuccessful = ({ branding, homePath }) => {
  const navigate = useNavigate();
  const { history, notificationFeed } = useContext(DataContext);
  const urlParams = useParams();
  const [resendData, setResendData] = useState({});
  const hasSentSms = useRef(false);

  const transfer = history?.find(({ id }) => id === urlParams.id);
  const notification = notificationFeed?.find(
    ({ transfer_id }) => transfer_id === urlParams.id
  );

  // notification/feed doesn't work locally, uncomment an option below

  // // ON NETWORK
  // const notification = notificationFeed.find(
  //   ({ transfer_id }) => transfer_id === "17bfb8a4-2fa3-5615-b4b4-e3cc6151c65d"
  // );

  // const transfer = {
  //   id: urlParams.id,
  //   on_network: true,
  //   status: notification?.status?.toUpperCase(),
  // };

  // // OFF NETWORK
  // const notification = notificationFeed.find(
  //   ({ transfer_id }) => transfer_id === "c35bc840-1865-5bf5-916d-b85ce20589c1"
  // );

  // const transfer = {
  //   amount: notification?.amount,
  //   id: urlParams.id,
  //   note: "Dummy data OFF network succeeded",
  //   on_network: false,
  //   // recipient: { type: "phone", value: "+16155551234" },
  //   recipient: { type: "email", value: "dummy@test.com" },
  //   sender_name: "Chet Wallsworth",
  //   status: "PENDING",
  //   timestamp: "2024-05-21T15:48:54.181114",
  // };
  const canSendSMS = isSMSCapable();

  const shouldShowSmsSend = () => {
    if (transfer !== undefined) {
      return (
        transfer.recipient?.type === "phone" &&
        canSendSMS &&
        (notification?.shortlink ||
          (notification?.status === "succeeded" &&
            branding?.send_on_network_notification))
      );
    }
    return false;
  };

  useEffect(() => {
    if (notification?.shortlink) {
      setResendData({
        identifier: transfer?.recipient?.value,
        children: "",
        disabled: false,
        message: `${branding.sms_customization.off_net_first} $${transfer?.amount?.value} ${branding.sms_customization.off_net_second} ${notification?.financial_institution} ${branding.sms_customization.off_net_third} "${transfer?.note}". ${branding.sms_customization.off_net_fourth} ${notification?.shortlink}`,
      });
    } else if (notification?.status === "succeeded") {
      setResendData({
        identifier: transfer?.recipient?.value,
        children: "",
        disabled: false,
        message: `${branding.sms_customization.on_net_first} $${transfer?.amount?.value} ${branding.sms_customization.on_net_second} "${transfer?.note}". ${branding.sms_customization.on_net_third}`,
      });
    }
  }, [branding, notification, transfer]);

  useEffect(() => {
    // Only attempt to send SMS once, and only when we have all the necessary data
    // Critical: Check resendData.message exists to prevent race condition with the previous useEffect
    if (shouldShowSmsSend() && !hasSentSms.current && resendData.message && resendData.identifier) {
      // Add a small delay to ensure the DOM has updated with the instructions
      const timeoutId = setTimeout(() => {
        hasSentSms.current = true;

        try {
          if (notification?.shortlink) {
            clickSendSms(
              resendData.message,
              resendData.identifier
            );
          } else if (branding?.send_on_network_notification) {
            clickSendSms(
              resendData.message,
              resendData.identifier
            );
          } else {
            console.warn("sms data issue: missing shortlink or on_network_notification flag");
          }
        } catch (error) {
          console.error("Failed to trigger SMS:", error);
          // Reset flag so user can manually retry via button
          hasSentSms.current = false;
        }
      }, 100); // Small delay to ensure resendData is fully set and DOM is updated

      return () => clearTimeout(timeoutId);
    }
  }, [shouldShowSmsSend, resendData, notification?.shortlink, branding?.send_on_network_notification]);

  const clickCopy = () => {
    navigator.clipboard.writeText(resendData.message);
    document.getElementById("show_click_copy").style.display = "block";
    setTimeout(() => {
      document.getElementById("show_click_copy").style.display = "none";
    }, 1000);
  };

  const smallSuccessIcon = () => {
    return (
      <Check
        style={{
          height: "auto",
          width: "40px",
          margin: "-35px 0 0 60px",
          zIndex: 1,
          borderRadius: "20px",
        }}
        className="fill_sending_success_color stroke_sending_bg_primary bg_sending_bg_primary"
      />
    );
  };

  const { renderDoubleIcon } = DoubleIcon(smallSuccessIcon);

  const nonDetailScreen = () => {
    return (
      <div
        className="center_page"
        style={{
          height: "65%",
          flexDirection: "column",
        }}
      >
        <h1>Success</h1>
        <div
          data-cy="transfer_processed_message"
          style={{ margin: "-15px 0 25px 0", fontSize: "16px" }}
        >
          Payment was sent
        </div>
        {renderDoubleIcon()}
      </div>
    );
  };

  const detailScreen = () => {
    return (
      <div>
        <h1
          data-cy="transfer_processed_message"
          style={{ margin: 0, lineHeight: 1.3 }}
        >
          {transfer.on_network ? (
            <> Your transfer has been sent!</>
          ) : (
            <> Your transfer is ready to be sent!</>
          )}
        </h1>
        <div className="transfer_action_info" style={{ marginBottom: "15px" }}>
          <div style={{ marginRight: "10px" }}>
            <Info className="fill_sending_bg_primary stroke_sending_bg_secondary" />
          </div>
          {transfer.recipient?.type === "phone" ? (
            <>
              Please text ("SMS") the instructions below to the recipient to
              claim funds...
            </>
          ) : (
            <>Instructions have been sent to recipient's email</>
          )}
        </div>
        <h3 style={{ marginTop: "20px" }}>Instructions for your recipient</h3>
        <div
          className="status"
          style={{
            marginBottom: "15px",
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "16px",
          }}
        >
          <span>
            {notification?.shortlink ? (
              <>
                {resendData.message?.split("http")[0]}
                <strong style={{ textDecoration: "underline" }}>
                  {notification.shortlink}
                </strong>
              </>
            ) : (
              <>{resendData.message}</>
            )}
          </span>
          <div style={{ textAlign: "right" }}>
            <Copy
              className="fill_sending_bg_tertiary stroke_sending_bg_secondary"
              onClick={() => clickCopy()}
              style={{ cursor: "pointer" }}
            />
            <div id="show_click_copy" style={{ display: "none" }}>
              Message copied
            </div>
          </div>
        </div>
        {shouldShowSmsSend() && <SendSms {...resendData}></SendSms>}
        <div style={{ marginTop: "40px" }}>{TransferDetails(transfer)}</div>
      </div>
    );
  };

  return (
    <main className="page_without_nav">
      {transfer?.status ? (
        <>
          {transfer.on_network && !branding.send_on_network_notification ? (
            nonDetailScreen()
          ) : (
            <div
              style={{
                paddingBottom: "20px",
              }}
            >
              {detailScreen()}
            </div>
          )}
        </>
      ) : (
        <div className="center_page">Transfer Not Found</div>
      )}
      {BottomButton(() => navigate(homePath), "Return Home")}
    </main>
  );
};

export default TransferSuccessful;
